/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { simpleFetch } from "../../../restWrapper";
import { getProjectTitle } from "../../../assets/projectTitleHelper";

function projectLabel(mission, language, unnamedFallback) {
  return getProjectTitle(language, mission) || unnamedFallback || "(untitled project)";
}

function ProjectSearchPicker({ selected, onChange, disabled, languageObj, subDomain, language }) {
  const lang = languageObj || {};
  const placeholder = lang.projectPlaceholder || "Search by project name";
  const loadingText = lang.projectLoading || "Loading projects…";
  const noResultsTpl = lang.projectNoResults || 'No projects found for "{q}"';
  const noneAvailable = lang.projectNoneAvailable || "No projects available";
  const lockTooltip = lang.projectLockTooltip || "Upgrade to Premium to filter by project";
  const unnamed = lang.projectUnnamed || "(untitled project)";
  const closedLabel = lang.projectClosedSuffix || "Closed";

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (disabled || loaded || loading || !subDomain) return;
    setLoading(true);
    let all = [];
    const step = (lastId) =>
      simpleFetch("/projects", "GET", lastId ? { subDomain, lastId } : { subDomain }).then((res) => {
        const page = res && res.status === 200 && Array.isArray(res.body) ? res.body : [];
        if (page.length === 0) return;
        all = all.concat(page);
        return step(page[page.length - 1].id);
      });
    step("")
      .catch(() => {})
      .then(() => {
        setProjects(all);
        setLoading(false);
        setLoaded(true);
      });
  }, [disabled, subDomain]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isSelected = (key) => selected.some((c) => c.key === key);

  const toggle = (mission) => {
    const key = mission.id;
    if (isSelected(key)) {
      onChange(selected.filter((c) => c.key !== key));
    } else {
      onChange([...selected, { key, label: projectLabel(mission, language, unnamed) }]);
    }
  };

  const removeChip = (key) => {
    onChange(selected.filter((c) => c.key !== key));
  };

  const trimmedQuery = query.trim().toLowerCase();
  const filtered = trimmedQuery
    ? projects.filter((m) => projectLabel(m, language, unnamed).toLowerCase().includes(trimmedQuery))
    : projects;

  return (
    <div
      className="ProjectSearchPicker"
      ref={containerRef}
      title={disabled ? lockTooltip : undefined}
    >
      {selected.length > 0 && (
        <div className="ProjectSearchPicker-chips">
          {selected.map((c) => (
            <span key={c.key} className="ProjectSearchPicker-chip">
              <span className="ProjectSearchPicker-chipLabel">{c.label}</span>
              {!disabled && (
                <button
                  type="button"
                  className="ProjectSearchPicker-chipRemove"
                  onClick={() => removeChip(c.key)}
                  aria-label="Remove"
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        className="ProjectSearchPicker-input"
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpen(true)}
      />
      {open && !disabled && (
        <div className="ProjectSearchPicker-dropdown">
          {loading && <div className="ProjectSearchPicker-empty">{loadingText}</div>}
          {!loading && filtered.length === 0 && (
            <div className="ProjectSearchPicker-empty">
              {trimmedQuery ? noResultsTpl.replace("{q}", query.trim()) : noneAvailable}
            </div>
          )}
          {!loading &&
            filtered.map((mission) => (
              <label key={mission.id} className="ProjectSearchPicker-row">
                <input
                  type="checkbox"
                  checked={isSelected(mission.id)}
                  onChange={() => toggle(mission)}
                />
                <span className="ProjectSearchPicker-rowLabel">
                  {projectLabel(mission, language, unnamed)}
                </span>
                {mission.status === "closed" && (
                  <span className="ProjectSearchPicker-rowStatus">{closedLabel}</span>
                )}
              </label>
            ))}
        </div>
      )}
    </div>
  );
}

ProjectSearchPicker.propTypes = {
  selected: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  languageObj: PropTypes.object,
  subDomain: PropTypes.string,
  language: PropTypes.string,
};

export default ProjectSearchPicker;
